import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import ConnectionError from '../../../components/ConnectionError/ConnectionError';
import EmptyState from '../../../components/EmptyState/EmptyState';
import { adminService } from '../../../services/adminService';
import { getErrorMessage } from '../../../utils/errors';
import type { Role, UserRecord, UserStatisticsResponse } from '../../../types';
import { UserCards } from '../../../components/dashboard/users/UserCards';
import { UserTable } from '../../../components/dashboard/users/UserTable';
import { ConfirmationModal } from '../../../components/common/ConfirmationModal/ConfirmationModal';
import './UsersPage.css';

interface UserEditForm {
  name: string;
  email: string;
  role: Role;
}

const EMPTY_EDIT_FORM: UserEditForm = {
  name: '',
  email: '',
  role: 'USER',
};

function UsersPage() {
  const { t } = useTranslation();
  const { user, hasRole } = useAuth();
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  
  // Search & Role filtering state
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  
  // Stats state
  const [stats, setStats] = useState<UserStatisticsResponse | null>(null);
  
  // Inline edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<UserEditForm>(EMPTY_EDIT_FORM);
  
  // Reusable confirmation modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<{ id: string; name: string } | null>(null);
  
  const isAdmin = hasRole('ADMIN');

  // Load both users and statistics concurrently from the backend
  const loadUsersAndStats = useCallback(async (currentSearch: string, currentRole: string, currentPage: number) => {
    setLoading(true);
    setError(false);
    try {
      const [usersData, statsData] = await Promise.all([
        adminService.getUsers(currentSearch, currentRole, currentPage, size),
        adminService.getUsersStatistics()
      ]);
      setUsers(usersData.content);
      setTotalPages(usersData.totalPages);
      setTotalElements(usersData.totalElements);
      setStats(statsData);
    } catch {
      setError(true);
    }
    setLoading(false);
  }, [size]);

  // Debounce search input to avoid spamming the backend
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0); // Reset to first page when search changes
    }, 350);
    return () => clearTimeout(handler);
  }, [search]);

  // Fetch users when debounced search query, role filter, or current page changes
  useEffect(() => {
    loadUsersAndStats(debouncedSearch, roleFilter, page);
  }, [debouncedSearch, roleFilter, page, loadUsersAndStats]);

  const handleEdit = (u: UserRecord) => {
    setEditingId(u.id);
    setEditForm({ name: u.name, email: u.email, role: u.role });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm(EMPTY_EDIT_FORM);
  };

  const handleFormChange = (field: keyof UserEditForm, value: any) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (userId: string) => {
    try {
      await adminService.updateUser(userId, editForm);
      toast.success(t('admin.userUpdated'));
      setEditingId(null);
      loadUsersAndStats(debouncedSearch, roleFilter, page);
    } catch (error) {
      toast.error(await getErrorMessage(error, t('admin.updateFailed')));
    }
  };

  const handleDeleteClick = (userId: string, name: string) => {
    setUserToDelete({ id: userId, name });
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    try {
      await adminService.deleteUser(userToDelete.id);
      toast.success(t('admin.userDeleted'));
      setDeleteModalOpen(false);
      setUserToDelete(null);
      loadUsersAndStats(debouncedSearch, roleFilter, page);
    } catch (error) {
      toast.error(await getErrorMessage(error, t('admin.deleteFailed')));
    }
  };

  const formatDate = (dateStr?: string | null): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  };

  if (loading && users.length === 0) {
    return (
      <div className="admin-loading">
        <span className="auth-spinner auth-spinner-brand"></span>
      </div>
    );
  }

  if (error) {
    return <ConnectionError onRetry={() => loadUsersAndStats(debouncedSearch, roleFilter, page)} />;
  }

  return (
    <div className="admin-users-page-container">
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">{t('admin.users')}</h1>
          <p className="admin-page-subtitle">{t('admin.usersDesc')}</p>
        </div>
      </div>

      {/* Stats Cards Section (extracted component) */}
      <UserCards stats={stats} />

      {/* Integrated Table Card Block */}
      <div className="admin-table-card">
        {/* Table Header Toolbar (Search + Role Filter) */}
        <div className="admin-table-header">
          <div className="admin-filter-controls">
            <div className="admin-search-wrapper">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                placeholder={t('admin.searchUsers')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select
              className="admin-role-filter-select"
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setPage(0); // Reset to first page when filter changes
              }}
            >
              <option value="">{t('admin.allRoles', 'All Roles')}</option>
              <option value="USER">{t('admin.regularUsers', 'Regular Users')}</option>
              <option value="ADMIN">{t('admin.admins', 'Admins')}</option>
            </select>
          </div>
        </div>

        {users.length === 0 ? (
          <div style={{ padding: '48px 24px' }}>
            <EmptyState
              icon={
                <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                </svg>
              }
              title={debouncedSearch || roleFilter ? t('errors.noResults') : t('errors.noUsers')}
              description={debouncedSearch || roleFilter ? t('errors.noResultsDesc') : t('errors.noUsersDesc')}
            />
          </div>
        ) : (
          <>
            {/* Scrollable responsive table element (extracted component) */}
            <UserTable
              users={users}
              isAdmin={isAdmin}
              currentUserId={user?.userId}
              editingId={editingId}
              editForm={editForm}
              onEdit={handleEdit}
              onCancelEdit={handleCancelEdit}
              onSave={handleSave}
              onDeleteClick={handleDeleteClick}
              onFormChange={handleFormChange}
              formatDate={formatDate}
            />

            {/* Table Footer Toolbar (Enhanced Pagination) */}
            <div className="admin-table-footer">
              <div className="pagination-info">
                {t('admin.showingEntries', 'Showing {{start}} to {{end}} of {{total}} entries', {
                  start: page * size + 1,
                  end: Math.min((page + 1) * size, totalElements),
                  total: totalElements
                })}
              </div>
              <div className="pagination-buttons">
                <button
                  className="pagination-btn"
                  disabled={page === 0}
                  onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                >
                  {t('admin.previous', 'Previous')}
                </button>
                <span className="pagination-page-indicator">
                  {page + 1} / {totalPages || 1}
                </span>
                <button
                  className="pagination-btn"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
                >
                  {t('admin.next', 'Next')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Reusable Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        title={t('admin.confirmDeleteTitle', 'Confirm Deletion')}
        message={t('admin.confirmDeleteUser', 'Are you sure you want to delete {{name}}?', { name: userToDelete?.name })}
        confirmLabel={t('admin.delete', 'Delete')}
        cancelLabel={t('admin.cancel', 'Cancel')}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setDeleteModalOpen(false);
          setUserToDelete(null);
        }}
        type="danger"
      />
    </div>
  );
}

export default UsersPage;
